if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.handleErrorMessage();

        const originalFormData = new FormData(this.form);
        const productVariantId = originalFormData.get('id');
        const quantity = originalFormData.get('quantity') || 1;

        /*
         * Find the optional Ravgaard chain add-on
         * inside this specific product form.
         */
        const chainAddon = this.querySelector('[data-chain-addon]');
        const selectedChain = chainAddon
          ? chainAddon.querySelector('[data-chain-radio]:checked')
          : null;

        let selectedChainVariant = null;

        if (selectedChain) {
          const selectedCard = selectedChain.closest('[data-chain-card]');

          selectedChainVariant = selectedCard
            ? selectedCard.querySelector('[data-chain-variant]:checked')
            : null;

          /*
           * A chain has been selected, but no length has been chosen.
           */
          if (!selectedChainVariant) {
            const lengthError =
              chainAddon.dataset.lengthError || 'Choose length';

            this.handleErrorMessage(lengthError);

            const variantsArea = selectedCard
              ? selectedCard.querySelector('[data-chain-variants]')
              : null;

            if (variantsArea) {
              variantsArea.hidden = false;
              variantsArea.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }

            return;
          }
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        delete config.headers['Content-Type'];

        let formData;

        /*
         * If a chain and length are selected, send the pendant
         * and chain as two separate cart items.
         */
        if (selectedChainVariant) {
          formData = new FormData();

          formData.append('items[0][id]', productVariantId);
          formData.append('items[0][quantity]', quantity);

          /*
           * Preserve product properties, selling plans and other
           * relevant fields from the original product form.
           */
          originalFormData.forEach((value, key) => {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);

            if (propertyMatch) {
              formData.append(
                `items[0][properties][${propertyMatch[1]}]`,
                value
              );
            }

            if (key === 'selling_plan' && value) {
              formData.append('items[0][selling_plan]', value);
            }
          });

          formData.append(
            'items[1][id]',
            selectedChainVariant.value
          );

          formData.append('items[1][quantity]', 1);
        } else {
          /*
           * No chain selected. Use Dawn's normal product form.
           */
          formData = originalFormData;
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart
              .getSectionsToRender()
              .map((section) => section.id)
          );

          formData.append(
            'sections_url',
            window.location.pathname
          );

          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId,
                errors:
                  response.errors || response.description,
                message: response.message,
              });

              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(
                  '.sold-out-message'
                );

              if (!soldOutMessage) {
                return;
              }

              this.submitButton.setAttribute(
                'aria-disabled',
                true
              );

              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');

              this.error = true;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker(
                'add:wait-for-subscribers'
              );

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });
            }

            this.error = false;

            const quickAddModal =
              this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              CartPerformance.measure(
                'add:paint-updated-sections',
                () => {
                  this.cart.renderContents(response);
                }
              );
            }
          })
          .catch((error) => {
            console.error(error);

            this.handleErrorMessage(
              'Something went wrong. Please try again.'
            );
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');

            if (
              this.cart &&
              this.cart.classList.contains('is-empty')
            ) {
              this.cart.classList.remove('is-empty');
            }

            if (!this.error) {
              this.submitButton.removeAttribute(
                'aria-disabled'
              );
            }

            if (spinner) {
              spinner.classList.add('hidden');
            }

            CartPerformance.measureFromEvent(
              'add:user-action',
              evt
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) {
          return;
        }

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(
            '.product-form__error-message-wrapper'
          );

        if (!this.errorMessageWrapper) {
          return;
        }

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute(
          'hidden',
          !errorMessage
        );

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute(
            'disabled',
            'disabled'
          );

          if (text) {
            this.submitButtonText.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute('disabled');

          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}